import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, Button } from '../../../shared/components/ui';

const checklistItems = [
  {
    id: 'upload',
    key: 'hasUploadedHistoricalEstimates',
    title: 'Upload historical estimates',
    description: 'Import your past estimates so the AI can learn from your pricing history.',
    action: '/estimates/upload',
    actionLabel: 'Upload',
  },
  {
    id: 'create',
    key: 'hasCreatedFirstEstimate',
    title: 'Create your first AI-assisted estimate',
    description: 'Create a new estimate and see how the AI suggests pricing based on similar projects.',
    action: '/estimates/new',
    actionLabel: 'Create Estimate',
  },
  {
    id: 'rules',
    key: 'hasAdjustedPricingRules',
    title: 'Adjust pricing rules',
    description: 'Fine-tune how the AI calculates costs for your business.',
    action: '/settings/pricing',
    actionLabel: 'Configure',
  },
];

function ChecklistItem({ item, isComplete, onAction }) {
  return (
    <div className={`flex items-start gap-4 p-4 rounded-lg ${isComplete ? 'bg-green-50 dark:bg-green-900/30' : 'bg-gray-50 dark:bg-gray-900'}`}>
      {/* Checkbox */}
      <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
        isComplete
          ? 'bg-green-500 border-green-500'
          : 'border-gray-300 dark:border-gray-700'
      }`}>
        {isComplete && (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h4 className={`text-sm font-medium ${isComplete ? 'text-green-800 dark:text-green-300 line-through' : 'text-gray-900 dark:text-gray-100'}`}>
          {item.title}
        </h4>
        <p className={`text-xs mt-1 ${isComplete ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
          {item.description}
        </p>
      </div>

      {/* Action */}
      {!isComplete && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onAction(item.action)}
        >
          {item.actionLabel}
        </Button>
      )}
    </div>
  );
}

export function OnboardingChecklist({ state = {}, onDismiss }) {
  const navigate = useNavigate();

  // Check if all items are complete
  const completedCount = checklistItems.filter((item) => state[item.key]).length;
  const isAllComplete = completedCount === checklistItems.length;

  // Don't render if dismissed or all complete
  if (isAllComplete) {
    return null;
  }

  const handleAction = (path) => {
    navigate(path);
  };

  return (
    <Card className="mb-8 border-blue-200 dark:border-blue-800 bg-blue-50/50">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-blue-900 dark:text-blue-200">Get Started</CardTitle>
          <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
            Complete these steps to get the most out of your pricing intelligence.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">
            {completedCount} of {checklistItems.length} complete
          </span>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="text-blue-400 hover:text-blue-600"
              aria-label="Dismiss checklist"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {checklistItems.map((item) => (
            <ChecklistItem
              key={item.id}
              item={item}
              isComplete={state[item.key]}
              onAction={handleAction}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default OnboardingChecklist;
